// src/pages/TimelinePage.js
import React, { useState, useEffect } from "react";
import { ScheduleComponent, ViewsDirective, ViewDirective, ResourcesDirective, ResourceDirective, Inject, TimelineViews, TimelineMonth, DragAndDrop, Resize, Day, Week, WorkWeek, Month, Agenda } from '@syncfusion/ej2-react-schedule';
import { db } from "../firebase";
import { collection, getDocs, addDoc, query, where, doc, getDoc, updateDoc, deleteDoc, setDoc } from "firebase/firestore";
import '../css/TimelinePage.css';

const HolidayModal = ({ isOpen, onClose, onSave }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [name, setName] = useState('');

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Dodaj praznik</h2>
        <div className="form-group">
          <label>Naziv praznika:</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Unesite naziv praznika"
          />
        </div>
        <div className="form-group">
          <label>Datum početka:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Datum završetka:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="modal-actions">
          <button onClick={onClose}>Odustani</button>
          <button 
            onClick={() => {
              if (startDate && endDate && name) {
                onSave({ startDate, endDate, name });
                onClose();
              }
            }}
            disabled={!startDate || !endDate || !name}
          >
            Spremi
          </button>
        </div>
      </div>
    </div>
  );
};

const ManageHolidaysModal = ({ isOpen, onClose, holidays, onDelete, onEdit }) => {
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [editName, setEditName] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');

  if (!isOpen) return null;

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setEditName(holiday.Description);
    setEditStartDate(new Date(holiday.StartTime).toISOString().split('T')[0]);
    setEditEndDate(new Date(holiday.EndTime).toISOString().split('T')[0]);
  };

  const handleSaveEdit = () => {
    if (editingHoliday && editName && editStartDate && editEndDate) {
      onEdit(editingHoliday.Id, {
        Description: editName,
        StartTime: new Date(editStartDate),
        EndTime: new Date(editEndDate)
      });
      setEditingHoliday(null);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content holidays-modal">
        <h2>Upravljanje praznicima</h2>
        <div className="holidays-list">
          {holidays.map((holiday) => (
            <div key={holiday.Id} className="holiday-item">
              {editingHoliday?.Id === holiday.Id ? (
                <div className="holiday-edit-form">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Naziv praznika"
                  />
                  <input
                    type="date"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                  />
                  <input
                    type="date"
                    value={editEndDate}
                    onChange={(e) => setEditEndDate(e.target.value)}
                  />
                  <div className="holiday-actions">
                    <button onClick={handleSaveEdit}>Spremi</button>
                    <button onClick={() => setEditingHoliday(null)}>Odustani</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="holiday-info">
                    <span className="holiday-name">{holiday.Description}</span>
                    <span className="holiday-dates">
                      {new Date(holiday.StartTime).toLocaleDateString()} - {new Date(holiday.EndTime).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="holiday-actions">
                    <button onClick={() => handleEdit(holiday)}>Uredi</button>
                    <button onClick={() => onDelete(holiday.Id)} className="delete">Obriši</button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="modal-actions">
          <button onClick={onClose}>Zatvori</button>
        </div>
      </div>
    </div>
  );
};

const TimelinePage = () => {
  const [allEmployees, setAllEmployees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const scheduleObj = React.useRef(null);
  const scrollRef = React.useRef(null);
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
  const [isManageHolidaysModalOpen, setIsManageHolidaysModalOpen] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [availableYears, setAvailableYears] = useState([2025, 2026, 2027]);

  const fetchAvailableYears = async () => {
    try {
      // Dohvati sve zaposlenike
      const employeesSnapshot = await getDocs(collection(db, "employees"));
      const yearsSet = new Set([2025, 2026, 2027]); // Default godine
      
      // Za svakog zaposlenika provjeri koje godine postoje u yearlyCounters
      for (const empDoc of employeesSnapshot.docs) {
        const countersSnapshot = await getDocs(collection(db, `employees/${empDoc.id}/yearlyCounters`));
        countersSnapshot.docs.forEach(counterDoc => {
          const year = parseInt(counterDoc.id);
          if (!isNaN(year)) {
            yearsSet.add(year);
          }
        });
      }
      
      // Sortiraj godine silazno
      const sortedYears = Array.from(yearsSet).sort((a, b) => b - a);
      setAvailableYears(sortedYears);
      
      // Ako trenutno odabrana godina nije u listi, postavi na prvu dostupnu
      if (sortedYears.length > 0 && !sortedYears.includes(selectedYear)) {
        setSelectedYear(sortedYears[0]);
      }
    } catch (error) {
      console.error('Greška pri dohvaćanju dostupnih godina:', error);
      // Fallback na default godine
      setAvailableYears([2025, 2026, 2027]);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const empSnapshot = await getDocs(collection(db, "employees"));
      const eventSnapshot = await getDocs(collection(db, "events"));
      const deptSnapshot = await getDocs(collection(db, "departments"));

      const departmentsData = deptSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));

      const employeesData = await Promise.all(empSnapshot.docs.map(async doc => {
        const employeeData = doc.data();
        const countersSnapshot = await getDocs(collection(db, `employees/${doc.id}/yearlyCounters`));
        const yearlyCounters = {};
        
        countersSnapshot.docs.forEach(counterDoc => {
          yearlyCounters[counterDoc.id] = counterDoc.data();
        });

        return {
          Id: doc.id,
          Name: `${employeeData.firstName} ${employeeData.lastName}`,
          department: employeeData.department,
          defaultSickDays: employeeData.sickDays || 0,
          defaultVacationDays: employeeData.vacationDays || 20,
          yearlyCounters: yearlyCounters,
          imageUrl: employeeData.imageUrl || '/default-avatar.png',
          lastName: employeeData.lastName
        };
      }));

      console.log("Dohvaćeni zaposlenici:", employeesData);

      // Sortiranje zaposlenika po odjelu i prezimenu
      const sortedEmployees = [...employeesData].sort((a, b) => {
        // Prvo sortiranje po odjelu
        const deptA = departmentsData.find(d => d.id === a.department)?.name || '';
        const deptB = departmentsData.find(d => d.id === b.department)?.name || '';
        
        if (deptA !== deptB) {
          return deptA.localeCompare(deptB);
        }
        
        // Ako su u istom odjelu, sortiraj po prezimenu
        return a.lastName.localeCompare(b.lastName);
      });

      const eventsData = eventSnapshot.docs.flatMap(doc => {
        const data = doc.data();
        const eventStartYear = new Date(data.start).getFullYear();
        const eventEndYear = new Date(data.end).getFullYear();
        
        // Filtriraj evente po odabranoj godini - prikaži samo one koji se preklapaju s odabranom godinom
        if (eventStartYear > selectedYear || eventEndYear < selectedYear) {
          return [];
        }
        
        if (data.employeeId === "*") {
          // Za praznike, kreiraj event za svakog zaposlenika
          return sortedEmployees.map(emp => ({
            Id: doc.id + "_" + emp.Id,
            Subject: "Praznik",
            Description: data.description,
            StartTime: new Date(data.start),
            EndTime: new Date(data.end),
            ResourceId: emp.Id,
            IsAllDay: true,
            CategoryColor: "#28a745",
            IsReadonly: true
          }));
        } else {
          // Za obične evente
          return [{
            Id: doc.id,
            Subject: data.reason,
            Description: data.description || '',
            StartTime: new Date(data.start),
            EndTime: new Date(data.end),
            ResourceId: data.employeeId,
            IsAllDay: true,
            CategoryColor: data.reason === "Godišnji" ? "#1a75ff" : 
                         data.reason === "Bolovanje" ? "#dc3545" :
                         data.reason === "Rad od kuće" ? "#ffd700" :
                         "#28a745"
          }];
        }
      });

      setDepartments(departmentsData);
      setAllEmployees(sortedEmployees);
      setEmployees(sortedEmployees);
      setEvents(eventsData);
    } catch (error) {
      console.error("Greška pri dohvaćanju podataka:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchAvailableYears();
  }, [selectedYear]);

  useEffect(() => {
    const handleScroll = (e) => {
      if (e.target.classList.contains('timeline-scroll')) {
        const scheduleContent = document.querySelector('.e-content-wrap');
        if (scheduleContent) {
          scheduleContent.scrollLeft = e.target.scrollLeft;
        }
      } else if (e.target.classList.contains('e-content-wrap')) {
        const topScroll = document.querySelector('.timeline-scroll');
        if (topScroll) {
          topScroll.scrollLeft = e.target.scrollLeft;
        }
      }
    };

    const topScroll = document.querySelector('.timeline-scroll');
    const scheduleContent = document.querySelector('.e-content-wrap');

    if (topScroll) {
      topScroll.addEventListener('scroll', handleScroll);
    }
    if (scheduleContent) {
      scheduleContent.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (topScroll) {
        topScroll.removeEventListener('scroll', handleScroll);
      }
      if (scheduleContent) {
        scheduleContent.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const dateHeaderTemplate = (props) => {
    const date = new Date(props.date);
    const day = date.getDay();
    const isWeekend = day === 0 || day === 6;
    const dayNames = ['Ned', 'Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub'];
    
    return (
      <div className={`date-header ${isWeekend ? 'weekend' : ''}`} style={{ textAlign: 'center', padding: '8px 0' }}>
        <div className="day-name" style={{ fontSize: '12px', fontWeight: '500' }}>{dayNames[day]}</div>
        <div className="day-number" style={{ fontSize: '14px', marginTop: '2px' }}>
          {date.getDate().toString().padStart(2, '0')}.{(date.getMonth() + 1).toString().padStart(2, '0')}
        </div>
      </div>
    );
  };

  const resourceHeaderTemplate = (props) => {
    const employee = props.resourceData;
    
    // Dohvati counter za odabranu godinu
    let yearCounter = employee.yearlyCounters?.[selectedYear];
    
    // Ako nema countera za odabranu godinu, koristi defaultne vrijednosti
    // Counter će se kreirati automatski kada se prvi put doda event za tu godinu
    // Prvo provjeri defaultVacationDays za tu godinu, zatim globalni default
    const defaultForYear = yearCounter?.defaultVacationDays ?? employee.defaultVacationDays ?? 20;
    const vacationDaysLeft = yearCounter?.vacationDaysLeft ?? defaultForYear;
    const sickDays = yearCounter?.sickDays ?? 0;
    const workFromHomeDays = yearCounter?.workFromHomeDays ?? 0;

    return (
      <div className="resource-details">
        <div className="resource-name">{employee.Name}</div>
        <div className="yearly-counters">
          <div className="year-counter">
            <span className="year">{selectedYear}:</span>
            <span className="vacation" style={{background: '#e3f2fd', color: '#1a75ff'}}>G{vacationDaysLeft}</span>
            <span className="sick" style={{background: '#fff5f5', color: '#dc3545'}}>B{sickDays}</span>
            <span className="work-from-home" style={{background: '#fff3cd', color: '#856404'}}>R{workFromHomeDays}</span>
          </div>
        </div>
      </div>
    );
  };

  const eventTemplate = (props) => {
    const style = {
      backgroundColor: props.Subject === "Godišnji" ? "#1a75ff" : 
                      props.Subject === "Bolovanje" ? "#dc3545" :
                      props.Subject === "Rad od kuće" ? "#ffd700" :
                      props.Subject === "Praznik" ? "#28a745" :
                      "#28a745",
      color: props.Subject === "Bolovanje" || props.Subject === "Rad od kuće" ? "black" : "white",
      padding: "4px 8px",
      height: "100%",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    };
                          
    return (
      <div style={style}>
        {props.Subject === "Praznik" ? props.Description : props.Subject}
      </div>
    );
  };

  const editorTemplate = (props) => {
    if (!props) return <div></div>;

    // Formatiraj datum za input type="date"
    const formatDateForInput = (date) => {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return (
      <table className="custom-event-editor" style={{ width: '100%', padding: '20px' }}>
        <tbody>
          <tr>
            <td className="e-textlabel">Vrsta odsustva *</td>
            <td style={{ padding: '10px' }}>
              <select 
                id="Subject"
                name="Subject"
                className="e-field e-input"
                data-name="Subject"
                defaultValue={props.Subject || "Godišnji"}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="Godišnji">Godišnji odmor</option>
                <option value="Bolovanje">Bolovanje</option>
                <option value="Rad od kuće">Rad od kuće</option>
              </select>
            </td>
          </tr>
          <tr>
            <td className="e-textlabel">Datum od *</td>
            <td style={{ padding: '10px' }}>
              <input
                type="date"
                id="StartTime"
                name="StartTime"
                className="e-field e-input"
                data-name="StartTime"
                defaultValue={formatDateForInput(props.StartTime)}
                style={{ width: '100%', padding: '8px' }}
              />
            </td>
          </tr>
          <tr>
            <td className="e-textlabel">Datum do *</td>
            <td style={{ padding: '10px' }}>
              <input
                type="date"
                id="EndTime"
                name="EndTime"
                className="e-field e-input"
                data-name="EndTime"
                defaultValue={formatDateForInput(props.EndTime)}
                style={{ width: '100%', padding: '8px' }}
              />
            </td>
          </tr>
          <tr>
            <td className="e-textlabel">Zaposlenik *</td>
            <td style={{ padding: '10px' }}>
              <select
                id="ResourceId"
                name="ResourceId"
                className="e-field e-input"
                data-name="ResourceId"
                defaultValue={props.ResourceId || (props.cellDetails?.groupIndex !== undefined ? employees[props.cellDetails.groupIndex]?.Id : '')}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">Odaberite zaposlenika</option>
                {employees.map(emp => (
                  <option key={emp.Id} value={emp.Id}>
                    {emp.Name}
                  </option>
                ))}
              </select>
            </td>
          </tr>
          <tr>
            <td className="e-textlabel">Napomena</td>
            <td style={{ padding: '10px' }}>
              <textarea
                id="Description"
                name="Description"
                className="e-field"
                data-name="Description"
                rows="3"
                defaultValue={props.Description || ''}
                placeholder="Dodajte napomenu..."
                style={{ width: '100%', padding: '8px', minHeight: '80px' }}
              />
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  const onRenderCell = (args) => {
    if (args.element.classList.contains('e-work-cells')) {
      const date = new Date(args.date);
      const day = date.getDay();
      if (day === 0 || day === 6) {
        args.element.classList.add('weekend-cell');
      }
    }
  };

  const calculateDaysBetweenDates = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 jer računamo i početni dan
  };

  const ensureYearlyCounter = async (employeeId, year) => {
    const counterRef = doc(db, `employees/${employeeId}/yearlyCounters/${year}`);
    const counterDoc = await getDoc(counterRef);
    
    if (!counterDoc.exists()) {
      // Dohvati defaultne vrijednosti zaposlenika
      const employeeDoc = await getDoc(doc(db, "employees", employeeId));
      if (employeeDoc.exists()) {
        const employeeData = employeeDoc.data();
        // Provjeri postoji li defaultVacationDays za tu godinu u nekom drugom counteru
        // Ako ne, koristi globalni default
        const defaultForYear = employeeData.vacationDays || 20;
        const initialData = {
          sickDays: 0,
          workFromHomeDays: 0,
          vacationDaysLeft: defaultForYear,
          defaultVacationDays: defaultForYear,
          year: parseInt(year)
        };
        await setDoc(counterRef, initialData);
      }
    }
    return counterRef;
  };

  const updateEmployeeCounters = async (employeeId, eventType, days, startDate, endDate, isAddition = true) => {
    try {
      const startYear = new Date(startDate).getFullYear();
      const endYear = new Date(endDate).getFullYear();
      
      // Ako event prelazi u drugu godinu, trebamo podijeliti dane
      if (startYear !== endYear) {
        const endOfStartYear = new Date(startYear, 11, 31);
        const daysInFirstYear = calculateDaysBetweenDates(startDate, endOfStartYear);
        const daysInSecondYear = days - daysInFirstYear;

        // Ažuriraj counter za prvu godinu
        await updateYearlyCounter(employeeId, eventType, startDate, endDate);
        // Ažuriraj counter za drugu godinu
        await updateYearlyCounter(employeeId, eventType, startDate, endDate);
      } else {
        // Event je unutar iste godine
        await updateYearlyCounter(employeeId, eventType, startDate, endDate);
      }
    } catch (error) {
      console.error("Greška pri ažuriranju countera:", error);
    }
  };

  const updateYearlyCounter = async (employeeId, eventType, startDate, endDate, isAddition = false) => {
    try {
      const employeeRef = doc(db, "employees", employeeId);
      const employeeDoc = await getDoc(employeeRef);
      
      if (!employeeDoc.exists()) {
        console.error("Zaposlenik nije pronađen");
        return;
      }

      const employee = employeeDoc.data();
      const startYear = new Date(startDate).getFullYear();
      const endYear = new Date(endDate).getFullYear();

      // Dohvati sve praznike iz baze
      const holidaysSnapshot = await getDocs(query(collection(db, "events"), where("reason", "==", "Praznik")));
      const allHolidays = holidaysSnapshot.docs.map(doc => ({
        start: new Date(doc.data().start),
        end: new Date(doc.data().end)
      }));

      // Funkcija za provjeru je li dan praznik
      const isHoliday = (date) => {
        return allHolidays.some(holiday => {
          const holidayStart = new Date(holiday.start);
          const holidayEnd = new Date(holiday.end);
          return date >= holidayStart && date <= holidayEnd;
        });
      };

      // Funkcija za računanje radnih dana za određenu godinu
      const calculateWorkDaysForYear = (start, end, targetYear) => {
        let workDays = 0;
        const currentDate = new Date(Math.max(start, new Date(targetYear, 0, 1)));
        const endDate = new Date(Math.min(end, new Date(targetYear, 11, 31)));

        while (currentDate <= endDate) {
          const currentYear = currentDate.getFullYear();
          if (currentYear === targetYear) {
            // Provjeri je li dan vikend (0 = nedjelja, 6 = subota)
            const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
            
            // Ako nije vikend i nije praznik, brojimo ga
            if (!isWeekend && !isHoliday(currentDate)) {
              workDays++;
            }
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        return workDays;
      };

      // Dohvati trenutne countere
      const countersRef = collection(db, `employees/${employeeId}/yearlyCounters`);
      const countersSnapshot = await getDocs(countersRef);
      const currentCounters = {};
      
      countersSnapshot.forEach(doc => {
        currentCounters[doc.id] = doc.data();
      });

      // Ako event prelazi u drugu godinu, trebamo ažurirati obje godine
      const yearsToUpdate = startYear === endYear ? [startYear] : [startYear, endYear];

      for (const year of yearsToUpdate) {
        // Ako nema countera za godinu, kreiraj defaultni
        if (!currentCounters[year]) {
          // Provjeri postoji li defaultVacationDays za tu godinu u bazi (može biti postavljen kroz modal)
          // Ako ne, koristi globalni default
          const counterRef = doc(db, `employees/${employeeId}/yearlyCounters/${year}`);
          const existingCounterDoc = await getDoc(counterRef);
          const existingDefault = existingCounterDoc.exists() ? existingCounterDoc.data().defaultVacationDays : null;
          const defaultForYear = existingDefault ?? employee.vacationDays ?? 20;
          
          currentCounters[year] = {
            vacationDaysLeft: defaultForYear,
            sickDays: 0,
            workFromHomeDays: 0,
            year: year,
            defaultVacationDays: defaultForYear
          };
        } else if (!currentCounters[year].defaultVacationDays) {
          // Ako counter postoji ali nema defaultVacationDays, dodaj ga
          currentCounters[year].defaultVacationDays = employee.vacationDays ?? 20;
        }

        // Izračunaj broj dana ovisno o tipu eventa
        let daysToAdd = 0;
        if (eventType === "Godišnji" || eventType === "Bolovanje") {
          daysToAdd = calculateWorkDaysForYear(startDate, endDate, year);
        } else {
          // Za work from home brojimo sve dane u toj godini
          const yearStart = new Date(Math.max(startDate, new Date(year, 0, 1)));
          const yearEnd = new Date(Math.min(endDate, new Date(year, 11, 31)));
          daysToAdd = Math.ceil((yearEnd - yearStart) / (1000 * 60 * 60 * 24)) + 1;
          if (daysToAdd < 0) daysToAdd = 0;
        }

        // Ažuriraj odgovarajući counter
        const counterRef = doc(countersRef, year.toString());
        const currentCounter = currentCounters[year];
        
        let updateData = {};
        if (eventType === "Godišnji") {
          updateData.vacationDaysLeft = (currentCounter.vacationDaysLeft || 0) + (isAddition ? daysToAdd : -daysToAdd);
        } else if (eventType === "Bolovanje") {
          updateData.sickDays = (currentCounter.sickDays || 0) + (isAddition ? daysToAdd : -daysToAdd);
        } else if (eventType === "Rad od kuće") {
          updateData.workFromHomeDays = (currentCounter.workFromHomeDays || 0) + (isAddition ? daysToAdd : -daysToAdd);
        }

        await setDoc(counterRef, {
          ...currentCounter,
          ...updateData
        }, { merge: true });

        console.log(`Counter ažuriran za ${eventType} u godini ${year}: ${isAddition ? '+' : '-'}${daysToAdd} dana`);
      }
    } catch (error) {
      console.error("Greška pri ažuriranju countera:", error);
    }
  };

  // Funkcija za ažuriranje countera kada se promijene praznici
  const updateCountersForHolidayChange = async () => {
    try {
      // Dohvati sve evente
      const eventsSnapshot = await getDocs(collection(db, "events"));
      const allEvents = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Dohvati sve zaposlenike
      const employeesSnapshot = await getDocs(collection(db, "employees"));
      const employees = employeesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Za svakog zaposlenika
      for (const employee of employees) {
        // Dohvati sve njegove evente
        const employeeEvents = allEvents.filter(event => 
          event.employeeId === employee.id && 
          (event.reason === "Godišnji" || event.reason === "Bolovanje" || event.reason === "Rad od kuće")
        );

        // Grupiraj evente po godinama
        const eventsByYear = {};
        employeeEvents.forEach(event => {
          const eventYear = new Date(event.start).getFullYear();
          if (!eventsByYear[eventYear]) {
            eventsByYear[eventYear] = [];
          }
          eventsByYear[eventYear].push(event);
        });

        // Za svaku godinu resetiraj countere i ponovno izračunaj
        for (const [year, yearEvents] of Object.entries(eventsByYear)) {
          const counterRef = doc(db, `employees/${employee.id}/yearlyCounters/${year}`);
          
          // Dohvati defaultVacationDays za tu godinu ako postoji
          const existingCounter = await getDoc(counterRef);
          const existingData = existingCounter.exists() ? existingCounter.data() : {};
          // Koristi postojeći defaultVacationDays ako postoji, inače globalni default
          const defaultForYear = existingData.defaultVacationDays ?? employee.vacationDays ?? 20;
          
          // Postavi početne vrijednosti - koristi postojeći vacationDaysLeft ako postoji, inače default
          await setDoc(counterRef, {
            vacationDaysLeft: existingData.vacationDaysLeft ?? defaultForYear,
            sickDays: 0,
            workFromHomeDays: 0,
            year: parseInt(year),
            defaultVacationDays: defaultForYear
          }, { merge: true });

          // Ponovno izračunaj sve evente za tu godinu
          for (const event of yearEvents) {
            if (event.reason === "Godišnji") {
              await updateYearlyCounter(
                employee.id,
                event.reason,
                event.start,
                event.end,
                false // oduzimamo dane
              );
            } else if (event.reason === "Bolovanje" || event.reason === "Rad od kuće") {
              await updateYearlyCounter(
                employee.id,
                event.reason,
                event.start,
                event.end,
                true // dodajemo dane
              );
            }
          }
        }
      }
    } catch (error) {
      console.error("Greška pri ažuriranju countera nakon promjene praznika:", error);
    }
  };

  // Modificiramo useEffect za dohvaćanje praznika da se ne osvježava nepotrebno
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const eventsRef = collection(db, "events");
        const q = query(eventsRef, where("reason", "==", "Praznik"));
        const querySnapshot = await getDocs(q);
        
        const holidaysData = querySnapshot.docs.map(doc => ({
          Id: doc.id,
          Subject: "Praznik",
          Description: doc.data().description,
          StartTime: new Date(doc.data().start),
          EndTime: new Date(doc.data().end),
          IsAllDay: true,
          CategoryColor: "#28a745",
          IsReadonly: true
        }));
        
        setHolidays(holidaysData);
      } catch (error) {
        console.error("Greška pri dohvaćanju praznika:", error);
      }
    };
    fetchHolidays();
  }, []); // Ostaje prazan dependency array

  const handleDeleteHoliday = async (holidayId) => {
    try {
      // Dodajemo potvrdni dijalog
      const holidayToDelete = holidays.find(h => h.Id === holidayId);
      if (!holidayToDelete) return;

      const confirmDelete = window.confirm(
        `Jeste li sigurni da želite obrisati praznik "${holidayToDelete.Description}"?\n` +
        `Period: ${new Date(holidayToDelete.StartTime).toLocaleDateString()} - ${new Date(holidayToDelete.EndTime).toLocaleDateString()}`
      );

      if (!confirmDelete) return;

      const eventRef = doc(db, "events", holidayId);
      await deleteDoc(eventRef);
      await updateCountersForHolidayChange();
      // Ažuriramo samo listu praznika
      const updatedHolidays = holidays.filter(h => h.Id !== holidayId);
      setHolidays(updatedHolidays);
    } catch (error) {
      console.error("Greška pri brisanju praznika:", error);
    }
  };

  const handleEditHoliday = async (holidayId, updatedData) => {
    try {
      const eventRef = doc(db, "events", holidayId);
      await updateDoc(eventRef, {
        description: updatedData.Description,
        start: updatedData.StartTime.toISOString(),
        end: updatedData.EndTime.toISOString()
      });
      await updateCountersForHolidayChange();
      
      // Ažuriramo samo listu praznika
      const updatedHolidays = holidays.map(h => {
        if (h.Id === holidayId) {
          return {
            ...h,
            Description: updatedData.Description,
            StartTime: updatedData.StartTime,
            EndTime: updatedData.EndTime
          };
        }
        return h;
      });
      setHolidays(updatedHolidays);
    } catch (error) {
      console.error("Greška pri ažuriranju praznika:", error);
    }
  };

  const addHoliday = async ({ startDate, endDate, name }) => {
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      const holidayData = {
        reason: "Praznik",
        description: name,
        start: start.toISOString(),
        end: end.toISOString(),
        employeeId: "*"
      };
      
      const docRef = await addDoc(collection(db, "events"), holidayData);
      
      // Dodajemo novi praznik u listu
      const newHoliday = {
        Id: docRef.id,
        Subject: "Praznik",
        Description: name,
        StartTime: start,
        EndTime: end,
        IsAllDay: true,
        CategoryColor: "#28a745",
        IsReadonly: true
      };
      setHolidays([...holidays, newHoliday]);
      
      // Ažuriramo countere
      await updateCountersForHolidayChange();
    } catch (error) {
      console.error("Greška pri dodavanju praznika:", error);
    }
  };

  const onActionBegin = async (args) => {
    try {
      if (args.requestType === 'eventRemove') {
        const eventData = args.data[0];
        const eventRef = doc(db, "events", eventData.Id);
        const eventDoc = await getDoc(eventRef);
        
        if (eventDoc.exists()) {
          const event = eventDoc.data();
          
          // Ako je praznik, ažuriraj sve countere
          if (event.reason === "Praznik") {
            await updateCountersForHolidayChange();
          } else {
            // Za ostale evente, suprotno djelujemo na counter
            if (event.reason === "Godišnji") {
              // Vraćamo dane u counter
              await updateYearlyCounter(
                event.employeeId,
                event.reason,
                event.start,
                event.end,
                true // vraćamo dane
              );
            } else if (event.reason === "Bolovanje" || event.reason === "Rad od kuće") {
              // Oduzimamo dane iz countera
              await updateYearlyCounter(
                event.employeeId,
                event.reason,
                event.start,
                event.end,
                false // oduzimamo dane
              );
            }
          }
          
          await deleteDoc(eventRef);
        }
      } else if (args.requestType === 'eventChange') {
        const eventData = args.data;
        const eventRef = doc(db, "events", eventData.Id);
        const oldEventDoc = await getDoc(eventRef);
        
        if (oldEventDoc.exists()) {
          const oldEvent = oldEventDoc.data();
          
          // Ako je promijenjen tip eventa ili datumi, ažuriraj countere
          if (
            oldEvent.reason !== eventData.Subject ||
            oldEvent.start !== eventData.StartTime.toISOString() ||
            oldEvent.end !== eventData.EndTime.toISOString()
          ) {
            // Prvo vratimo stare vrijednosti (suprotno djelovanje)
            if (oldEvent.reason === "Godišnji") {
              await updateYearlyCounter(
                oldEvent.employeeId,
                oldEvent.reason,
                oldEvent.start,
                oldEvent.end,
                true // vraćamo dane
              );
            } else if (oldEvent.reason === "Bolovanje" || oldEvent.reason === "Rad od kuće") {
              await updateYearlyCounter(
                oldEvent.employeeId,
                oldEvent.reason,
                oldEvent.start,
                oldEvent.end,
                false // oduzimamo dane
              );
            }
            
            // Zatim dodajemo nove vrijednosti
            if (eventData.Subject === "Godišnji") {
              await updateYearlyCounter(
                eventData.ResourceId,
                eventData.Subject,
                eventData.StartTime,
                eventData.EndTime,
                false // oduzimamo dane
              );
            } else if (eventData.Subject === "Bolovanje" || eventData.Subject === "Rad od kuće") {
              await updateYearlyCounter(
                eventData.ResourceId,
                eventData.Subject,
                eventData.StartTime,
                eventData.EndTime,
                true // dodajemo dane
              );
            }
          }
          
          // Ažuriraj event u bazi
          await updateDoc(eventRef, {
            start: eventData.StartTime.toISOString(),
            end: eventData.EndTime.toISOString(),
            employeeId: eventData.ResourceId,
            reason: eventData.Subject,
            description: eventData.Description || ''
          });
        }
      } else if (args.requestType === 'eventCreate') {
        const eventData = args.data[0];
        const startDate = new Date(eventData.StartTime);
        const endDate = new Date(eventData.EndTime);
        
        // Ažuriraj counter za novi event
        if (eventData.Subject === "Godišnji") {
          await updateYearlyCounter(
            eventData.ResourceId,
            eventData.Subject,
            startDate,
            endDate,
            false // oduzimamo dane
          );
        } else if (eventData.Subject === "Bolovanje" || eventData.Subject === "Rad od kuće") {
          await updateYearlyCounter(
            eventData.ResourceId,
            eventData.Subject,
            startDate,
            endDate,
            true // dodajemo dane
          );
        }
        
        // Spremi event u bazu
        const newEventData = {
          reason: eventData.Subject,
          description: eventData.Description || '',
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          employeeId: eventData.ResourceId,
        };
        await addDoc(collection(db, "events"), newEventData);
      }

      // Osvježi podatke samo ako je promjena uspješno završila i nije toolbarItemRendering
      if (args.requestType !== 'toolbarItemRendering' && 
          (args.requestType === 'eventRemove' || 
           args.requestType === 'eventChange' || 
           args.requestType === 'eventCreate')) {
        await fetchData();
      }
    } catch (error) {
      console.error("Greška pri upravljanju događajem:", error);
    }
  };

  const onEventRendered = (args) => {
    const eventElement = args.element;
    
    switch (args.data.Subject) {
      case 'Godišnji':
        eventElement.style.backgroundColor = '#1a75ff';
        eventElement.style.color = 'white';
        break;
      case 'Bolovanje':
        eventElement.style.backgroundColor = '#dc3545';
        eventElement.style.color = 'black';
        break;
      case 'Rad od kuće':
        eventElement.style.backgroundColor = '#ffd700';
        eventElement.style.color = 'black';
        break;
      case 'Praznik':
        eventElement.style.backgroundColor = '#28a745';
        eventElement.style.color = 'white';
        break;
      default:
        eventElement.style.backgroundColor = '#28a745';
        eventElement.style.color = 'white';
        break;
    }
  };

  const handleDepartmentFilter = (deptId) => {
    setSelectedDepartment(deptId);
    if (deptId === 'all') {
      setEmployees(allEmployees);
    } else {
      const filteredEmployees = allEmployees.filter(emp => emp.department === deptId);
      setEmployees(filteredEmployees);
    }
  };

  // Konfiguracija kalendara
  const eventSettings = {
    dataSource: events,
    fields: {
      id: 'Id',
      subject: { 
        name: 'Subject', 
        title: 'Vrsta odsustva',
        default: 'Godišnji',
        validation: { required: true }
      },
      description: { 
        name: 'Description', 
        title: 'Napomena',
        validation: { required: false }
      },
      startTime: { 
        name: 'StartTime', 
        title: 'Datum od',
        validation: { required: true }
      },
      endTime: { 
        name: 'EndTime', 
        title: 'Datum do',
        validation: { required: true }
      },
      isAllDay: { 
        name: 'IsAllDay', 
        default: true,
        title: 'Cijeli dan'
      }
    },
    template: eventTemplate,
    enableTooltip: true,
    tooltipTemplate: eventTemplate,
    editorTemplate: editorTemplate
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><p>Učitavanje...</p></div>;
  }

  return (
    <div className="page-container flex flex-col items-center w-full">
      <div className="timeline-container w-full">
        <style>
          {`
            .timeline-container {
              width: 100%;  
              height: 650px;
              display: flex;
              flex-direction: column;
              align-items: center;
            }

            .controls-container {
              width: 90% !important;
              margin-bottom: 1rem;
            }

            .schedule-container {
              width: 90%;
            }

            .timeline-scroll {
              width: 100%;
              overflow-x: auto;
              overflow-y: hidden;
              height: 20px;
              border-bottom: 1px solid #e0e0e0;
            }

            .timeline-scroll-content {
              height: 1px;
              min-width: calc(50px * 33);
            }

            .e-schedule {
              height: calc(100% - 20px);
              overflow-x: hidden;
              overflow-y: auto;
            }

            .e-schedule .e-timeline-month-view {
              overflow: visible;
            }

            .schedule-cell-dimension.e-schedule .e-timeline-month-view .e-content-wrap table col,
            .schedule-cell-dimension.e-schedule .e-timeline-view .e-content-wrap table col,
            .schedule-cell-dimension.e-schedule .e-timeline-day-view .e-content-wrap table col {
              width: 80px !important;
            }

            .e-schedule .e-timeline-month-view .e-work-cells,
            .e-schedule .e-timeline-view .e-work-cells,
            .e-schedule .e-timeline-day-view .e-work-cells {
              min-width: 80px !important;
              width: 80px !important;
              padding: 0 !important;
              border: 1px solid #e0e0e0 !important;
            }

            .e-schedule .e-timeline-month-view .e-header-cells,
            .e-schedule .e-timeline-view .e-header-cells,
            .e-schedule .e-timeline-day-view .e-header-cells {
              min-width: 80px !important;
              width: 80px !important;
              text-align: center;
              position: sticky;
              top: 0;
              z-index: 1;
              background: white;
              padding: 5px !important;
              border: 1px solid #e0e0e0 !important;
            }

            .e-schedule .e-timeline-month-view .e-date-header-wrap table col,
            .e-schedule .e-timeline-view .e-date-header-wrap table col,
            .e-schedule .e-timeline-day-view .e-date-header-wrap table col {
              width: 80px !important;
            }

            .e-schedule .e-timeline-month-view .e-header-row th,
            .e-schedule .e-timeline-view .e-header-row th,
            .e-schedule .e-timeline-day-view .e-header-row th {
              width: 80px !important;
            }

            .e-schedule .e-timeline-month-view .e-content-wrap .e-content-table col,
            .e-schedule .e-timeline-view .e-content-wrap .e-content-table col,
            .e-schedule .e-timeline-day-view .e-content-wrap .e-content-table col {
              width: 80px !important;
            }

            .e-schedule .e-timeline-month-view .e-content-wrap .e-content-table td,
            .e-schedule .e-timeline-view .e-content-wrap .e-content-table td,
            .e-schedule .e-timeline-day-view .e-content-wrap .e-content-table td {
              width: 80px !important;
            }

            .e-schedule .e-timeline-month-view .e-resource-cells,
            .e-schedule .e-timeline-view .e-resource-cells,
            .e-schedule .e-timeline-day-view .e-resource-cells {
              width: 250px !important;
              position: sticky;
              left: 0;
              z-index: 2;
              background: white;
              border: 1px solid #e0e0e0 !important;
            }

            .e-schedule .e-timeline-month-view .e-resource-left-td,
            .e-schedule .e-timeline-view .e-resource-left-td,
            .e-schedule .e-timeline-day-view .e-resource-left-td {
              width: 250px !important;
              position: sticky;
              left: 0;
              z-index: 2;
              background: white;
            }

            .e-schedule .e-timeline-month-view .e-left-indent-wrap,
            .e-schedule .e-timeline-view .e-left-indent-wrap,
            .e-schedule .e-timeline-day-view .e-left-indent-wrap {
              position: sticky;
              left: 0;
              z-index: 3;
              background: white;
            }

            .e-schedule .e-timeline-month-view .e-content-wrap,
            .e-schedule .e-timeline-view .e-content-wrap,
            .e-schedule .e-timeline-day-view .e-content-wrap {
              height: auto !important;
            }

            .e-schedule .e-timeline-month-view .e-content-wrap table,
            .e-schedule .e-timeline-view .e-content-wrap table,
            .e-schedule .e-timeline-day-view .e-content-wrap table {
              border-spacing: 0 !important;
              border-collapse: collapse !important;
              table-layout: fixed !important;
            }

            .e-schedule .e-timeline-month-view .e-resource-column table tbody tr {
              height: auto !important;
            }

            .e-schedule .e-timeline-month-view .e-content-wrap .e-content-table tr {
              height: auto !important;
            }

            .date-header {
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              padding: 2px;
            }

            .date-header .day-name {
              font-size: 11px !important;
              font-weight: 500;
            }

            .date-header .day-number {
              font-size: 12px !important;
              margin-top: 2px;
            }

            .date-header.weekend {
              background-color: #f8f9fa;
              color: #dc3545;
            }

            .weekend-cell {
              background-color: #f8f9fa !important;
            }

            .resource-name {
              font-weight: 500;
              font-size: 13px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-bottom: 4px;
            }

            .yearly-counters {
              display: flex;
              gap: 8px;
              align-items: center;
            }

            .year-counter {
              display: flex;
              align-items: center;
              gap: 4px;
              font-size: 12px;
              padding: 2px 4px;
              background: #f8f9fa;
              border-radius: 4px;
            }

            .year-counter .year {
              font-weight: 500;
              color: #666;
              font-size: 11px;
            }

            .year-counter .vacation,
            .year-counter .sick,
            .year-counter .work-from-home {
              padding: 2px 4px;
              border-radius: 3px;
              font-size: 11px;
              font-weight: 500;
            }

            .modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: rgba(0, 0, 0, 0.5);
              display: flex;
              justify-content: center;
              align-items: center;
              z-index: 1000;
            }

            .modal-content {
              background: white;
              padding: 20px;
              border-radius: 8px;
              width: 400px;
              max-width: 90%;
            }

            .modal-content h2 {
              margin-bottom: 20px;
              font-size: 1.5rem;
              font-weight: 500;
            }

            .form-group {
              margin-bottom: 15px;
            }

            .form-group label {
              display: block;
              margin-bottom: 5px;
              font-weight: 500;
            }

            .form-group input {
              width: 100%;
              padding: 8px;
              border: 1px solid #ddd;
              border-radius: 4px;
            }

            .modal-actions {
              display: flex;
              justify-content: flex-end;
              gap: 10px;
              margin-top: 20px;
            }

            .modal-actions button {
              padding: 8px 16px;
              border-radius: 4px;
              border: none;
              cursor: pointer;
            }

            .modal-actions button:first-child {
              background: #f8f9fa;
              color: #333;
            }

            .modal-actions button:last-child {
              background: #28a745;
              color: white;
            }

            .modal-actions button:disabled {
              opacity: 0.7;
              cursor: not-allowed;
            }

            .custom-schedule1 {
              border-top: 2px solid #d2222b !important;
              border-left: 2px solid #d2222b !important;
              border-right: 2px solid #d2222b !important;
              border-bottom: 2px solid #d2222b !important;
            }

            .holidays-modal {
              width: 600px;
              max-width: 90vw;
            }

            .holidays-list {
              max-height: 400px;
              overflow-y: auto;
              margin: 1rem 0;
            }

            .holiday-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 0.75rem;
              border: 1px solid #e0e0e0;
              border-radius: 4px;
              margin-bottom: 0.5rem;
            }

            .holiday-info {
              display: flex;
              flex-direction: column;
              gap: 0.25rem;
            }

            .holiday-name {
              font-weight: 500;
            }

            .holiday-dates {
              font-size: 0.875rem;
              color: #666;
            }

            .holiday-actions {
              display: flex;
              gap: 0.5rem;
            }

            .holiday-actions button {
              padding: 0.25rem 0.75rem;
              border-radius: 4px;
              border: none;
              cursor: pointer;
              font-size: 0.875rem;
            }

            .holiday-actions button:first-child {
              background: #1a75ff;
              color: white;
            }

            .holiday-actions button.delete {
              background: #dc3545;
              color: white;
            }

            .holiday-edit-form {
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
              width: 100%;
            }

            .holiday-edit-form input {
              padding: 0.5rem;
              border: 1px solid #ddd;
              border-radius: 4px;
            }

            .controls-container {
              display: flex;
              gap: 1rem;
              align-items: center;
            }

            .holiday-buttons {
              display: flex;
              gap: 0.5rem;
            }

            .holiday-buttons button {
              padding: 0.5rem 1rem;
              border-radius: 4px;
              border: none;
              cursor: pointer;
              font-weight: 500;
            }

            .holiday-buttons button:first-child {
              background: #28a745;
              color: white;
            }

            .holiday-buttons button:last-child {
              background: #1a75ff;
              color: white;
            }
          `}
        </style>
        <div className="controls-container mb-4 flex justify-between items-center w-full">
          <div className="filter-buttons flex gap-2 items-center">
            <div className="year-selector" style={{ marginRight: '1rem' }}>
              <label style={{ marginRight: '0.5rem', fontWeight: '500' }}>Godina:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <button
              className={selectedDepartment === 'all' ? 'active' : ''}
              onClick={() => handleDepartmentFilter('all')}
            >
              Svi odjeli
            </button>
            {departments.map(dept => (
              <button
                key={dept.id}
                className={selectedDepartment === dept.id ? 'active' : ''}
                onClick={() => handleDepartmentFilter(dept.id)}
              >
                {dept.name}
              </button>
            ))}
          </div>
          <div className="holiday-buttons">
            <button onClick={() => setIsHolidayModalOpen(true)}>
              Dodaj praznik
            </button>
            <button onClick={() => setIsManageHolidaysModalOpen(true)}>
              Upravljaj praznicima
            </button>
          </div>
        </div>

        <HolidayModal
          isOpen={isHolidayModalOpen}
          onClose={() => setIsHolidayModalOpen(false)}
          onSave={addHoliday}
        />

        <ManageHolidaysModal
          isOpen={isManageHolidaysModalOpen}
          onClose={() => setIsManageHolidaysModalOpen(false)}
          holidays={holidays}
          onDelete={handleDeleteHoliday}
          onEdit={handleEditHoliday}
        />

        <div className="timeline-scroll" ref={scrollRef}>
          <div className="timeline-scroll-content"></div>
        </div>
        <div className="schedule-container">
          <ScheduleComponent
            ref={scheduleObj}
            width='100%'
            height='650px'
            selectedDate={new Date(new Date().getTime() - (3 * 24 * 60 * 60 * 1000))}
            currentView="TimelineDay"
            eventSettings={eventSettings}
            resourceHeaderTemplate={resourceHeaderTemplate}
            dateHeaderTemplate={dateHeaderTemplate}
            renderCell={onRenderCell}
            actionBegin={onActionBegin}
            eventRendered={onEventRendered}
            timeScale={{ enable: false }}
            className="schedule-cell-dimension custom-schedule1"
            group={{ resources: ['Resources'] }}
            showTimeIndicator={false}
            timezone="UTC"
            showQuickInfo={false}
            editorTemplate={editorTemplate}
            allowDragAndDrop={true}
            allowResizing={true}
          >
            <ResourcesDirective>
              <ResourceDirective
                field='ResourceId'
                title='Zaposlenici'
                name='Resources'
                allowMultiple={false}
                dataSource={employees}
                textField='Name'
                idField='Id'
              />
            </ResourcesDirective>
            <ViewsDirective>
              <ViewDirective option='TimelineDay' interval={33}/>
            </ViewsDirective>
            <Inject services={[TimelineViews, TimelineMonth, DragAndDrop, Resize, Day, Week, WorkWeek, Month, Agenda]} />
          </ScheduleComponent>
        </div>
      </div>
    </div>
  );
};

export default TimelinePage;